import { Link } from "react-router-dom";
import { kaspreadsheetUrl } from "@/mocks/categories";

interface RelatedArticle {
  title: string;
  to: string;
}

interface ArticleLayoutProps {
  title: string;
  category: string;
  heroImg: string;
  heroImgAlt: string;
  children: React.ReactNode;
  relatedArticles?: RelatedArticle[];
}

export default function ArticleLayout({ title, category, heroImg, heroImgAlt, children, relatedArticles }: ArticleLayoutProps) {
  return (
    <div className="min-h-screen bg-background-50">
      <main>
        {/* Breadcrumb + Header */}
        <section className="pt-28 pb-8 md:pt-32 md:pb-12">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer">
                Strona główna
              </Link>
              <span className="text-foreground-400">/</span>
              <span className="text-foreground-700">Artykuły</span>
              <span className="text-foreground-400">/</span>
              <span className="text-accent-600 font-medium">{category}</span>
            </nav>

            <span className="inline-block bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              {category}
            </span>

            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8">
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-calendar-line"></i>
                </div>
                Zaktualizowano: Czerwiec 2026
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-line"></i>
                </div>
                Autor: Anna Kowalska
              </span>
              <span className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line"></i>
                </div>
                8 min czytania
              </span>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="pb-10">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <img
              src={heroImg}
              alt={heroImgAlt}
              className="w-full rounded-lg object-cover"
              loading="eager"
            />
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="prose-custom text-foreground-700 leading-relaxed space-y-6 text-base md:text-lg">
              {children}
            </div>

            {/* CTA inside article */}
            <div className="bg-accent-100 rounded-lg p-6 md:p-8 my-8">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-2">
                Chcesz kupić produkty USFansSpreadsheet?
              </h3>
              <p className="text-foreground-600 text-sm mb-4">
                Odwiedź oficjalny sklep Kaspreadsheet i sprawdź pełną ofertę produktów — od odzieży po akcesoria.
              </p>
              <a
                href={kaspreadsheetUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Przejdź do sklepu
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </div>
              </a>
            </div>

            {/* Author Box */}
            <div className="mt-12 p-5 md:p-6 bg-background-100 rounded-lg flex items-start gap-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-accent-200 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-accent-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-heading text-base font-semibold text-foreground-950">Anna Kowalska</h4>
                <p className="text-foreground-500 text-sm">Specjalista SEO, Badacz cyfrowy</p>
                <p className="text-foreground-600 text-sm mt-1 leading-relaxed">
                  Ekspertka w dziedzinie optymalizacji treści i analizy narzędzi cyfrowych. Regularnie publikuje poradniki i recenzje pomagające użytkownikom wybierać najlepsze rozwiązania technologiczne.
                </p>
              </div>
            </div>

            {/* Back to home */}
            <div className="mt-8 text-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-accent-600 hover:text-accent-700 text-sm font-medium transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line"></i>
                </div>
                ← Powrót do strony głównej
              </Link>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles && relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14 md:py-20">
            <div className="max-w-5xl mx-auto px-4 md:px-6">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
                Powiązane artykuły
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((item, i) => (
                  <Link
                    key={i}
                    to={item.to}
                    className="bg-background-50 rounded-lg p-5 hover:bg-background-200 transition-colors cursor-pointer"
                  >
                    <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                    <span className="inline-flex items-center gap-1 mt-3 text-accent-600 text-sm font-medium whitespace-nowrap">
                      Czytaj więcej
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-arrow-right-line text-xs"></i>
                      </div>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}